/**
 * Questionnaire construction and mutation utilities for file-driven templates.
 *
 * Pure functions that convert a ParsedTemplateFile into a FHIR Questionnaire,
 * handle signature extensions, and apply category metadata.
 */
import type { Questionnaire, Extension, UsageContext } from '@medplum/fhirtypes';
import {
  DOCUMENT_TEMPLATE_CATEGORY_SYSTEM,
  getDocumentCategoryLabel,
  isTemplateLibraryTabCode,
  libraryTabForDocumentCategory,
  TEMPLATE_LIBRARY_CATEGORY_SYSTEM,
} from '../documentTemplateCategories';
import { TEMPLATE_CATEGORY_LABELS } from '../../pages/settings/template-library/types';
import type { ParsedTemplateFile, TemplateFrontmatter } from './types';

export const SIGNATURE_REQUIRED_URL =
  'http://hl7.org/fhir/StructureDefinition/questionnaire-signatureRequired';

const DEFAULT_IDENTIFIER_SYSTEM =
  'https://unify.health/fhir/questionnaire-template';

const DEFAULT_URL_PREFIX =
  'https://unify.health/fhir/Questionnaire';

const CONTEXT_SYSTEM =
  'https://unify.health/fhir/template-context';

const TAG_SYSTEM =
  'https://unify.health/fhir/template-tag';

const RICH_TEXT_ITEM_CONTROL_URL =
  'http://hl7.org/fhir/StructureDefinition/questionnaire-itemControl';

/**
 * Build a Questionnaire from a parsed template file.
 */
export function buildQuestionnaireFromTemplate(
  parsed: ParsedTemplateFile,
): Questionnaire {
  const fm = parsed.frontmatter;
  const slug = fm.slug;

  let questionnaire: Questionnaire = {
    resourceType: 'Questionnaire',
    status: fm.status ?? 'active',
    title: fm.title,
    description: fm.description,
    version: fm.version != null ? String(fm.version) : undefined,
    language: fm.language ?? 'en-US',
    url: fm.url ?? `${DEFAULT_URL_PREFIX}/${slug}`,
    name: slug,
    identifier: [
      {
        system: fm.identifierSystem ?? DEFAULT_IDENTIFIER_SYSTEM,
        value: fm.identifierValue ?? slug,
      },
    ],
    publisher: fm.publisher ?? 'Unify Mental Health',
    subjectType: fm.subjectType ? [fm.subjectType as 'Patient'] : ['Patient'],
    item: [
      {
        linkId: 'body',
        type: 'text',
        text: fm.title,
        extension: [
          {
            url: RICH_TEXT_ITEM_CONTROL_URL,
            valueCodeableConcept: {
              coding: [
                {
                  system: 'http://hl7.org/fhir/questionnaire-item-control',
                  code: 'rich-text',
                  display: 'Rich Text',
                },
              ],
            },
          },
        ],
        initial: [{ valueString: parsed.bodyHtml }],
      },
    ],
  };

  questionnaire = applyTemplateCategory(
    questionnaire,
    fm.category,
    fm.templateLibraryTab,
  );
  questionnaire = applySignatureRequiredExtension(questionnaire, fm.requireSignature === true);
  questionnaire = applyTags(questionnaire, fm);
  questionnaire = applyUseContext(questionnaire, fm);

  return questionnaire;
}

/**
 * Apply the document template category via meta.tag and useContext.
 *
 * @param templateLibraryTab — optional override; must be a Template Library tab code (validated in `validateTemplate`).
 */
export function applyTemplateCategory(
  questionnaire: Questionnaire,
  category: string,
  templateLibraryTab?: string,
): Questionnaire {
  const label = getDocumentCategoryLabel(category) ?? category;
  const libraryTab =
    templateLibraryTab !== undefined &&
    templateLibraryTab !== '' &&
    isTemplateLibraryTabCode(templateLibraryTab)
      ? templateLibraryTab
      : libraryTabForDocumentCategory(category);
  const libraryTabLabel = TEMPLATE_CATEGORY_LABELS[libraryTab];

  const existingTags = (questionnaire.meta?.tag ?? []).filter(
    (t) =>
      t.system !== DOCUMENT_TEMPLATE_CATEGORY_SYSTEM &&
      t.system !== TEMPLATE_LIBRARY_CATEGORY_SYSTEM,
  );

  const categoryUseContext: UsageContext = {
    code: {
      system: CONTEXT_SYSTEM,
      code: 'category',
      display: 'Category',
    },
    valueCodeableConcept: {
      coding: [
        {
          system: DOCUMENT_TEMPLATE_CATEGORY_SYSTEM,
          code: category,
          display: label,
        },
      ],
    },
  };

  const existingUseContext = (questionnaire.useContext ?? []).filter(
    (uc) => !(uc.code?.system === CONTEXT_SYSTEM && uc.code?.code === 'category'),
  );

  return {
    ...questionnaire,
    meta: {
      ...questionnaire.meta,
      tag: [
        ...existingTags,
        {
          system: DOCUMENT_TEMPLATE_CATEGORY_SYSTEM,
          code: category,
          display: label,
        },
        {
          system: TEMPLATE_LIBRARY_CATEGORY_SYSTEM,
          code: libraryTab,
          display: libraryTabLabel,
        },
      ],
    },
    useContext: [...existingUseContext, categoryUseContext],
  };
}

/**
 * Add or remove the FHIR signature-required extension.
 * Preserves all unrelated extensions.
 */
export function applySignatureRequiredExtension(
  questionnaire: Questionnaire,
  required: boolean,
): Questionnaire {
  const existingExtensions = (questionnaire.extension ?? []).filter(
    (e) => e.url !== SIGNATURE_REQUIRED_URL,
  );

  if (!required) {
    return {
      ...questionnaire,
      extension: existingExtensions.length > 0 ? existingExtensions : undefined,
    };
  }

  const signatureExt: Extension = {
    url: SIGNATURE_REQUIRED_URL,
    valueCodeableConcept: {
      coding: [
        {
          system: 'urn:iso-astm:E1762-95:2013',
          code: '1.2.840.10065.1.12.1.1',
          display: "Author's Signature",
        },
      ],
    },
  };

  return {
    ...questionnaire,
    extension: [...existingExtensions, signatureExt],
  };
}

/**
 * Apply frontmatter tags to meta.tag.
 */
function applyTags(questionnaire: Questionnaire, fm: TemplateFrontmatter): Questionnaire {
  if (!fm.tags?.length) return questionnaire;

  const existingTags = questionnaire.meta?.tag ?? [];
  const existingNonGenericTags = existingTags.filter((t) => t.system !== TAG_SYSTEM);

  const newTags = fm.tags.map((tag) => ({
    system: TAG_SYSTEM,
    code: tag,
    display: tag,
  }));

  return {
    ...questionnaire,
    meta: {
      ...questionnaire.meta,
      tag: [...existingNonGenericTags, ...newTags],
    },
  };
}

/**
 * Apply useContext entries from frontmatter.useContext map.
 */
function applyUseContext(questionnaire: Questionnaire, fm: TemplateFrontmatter): Questionnaire {
  if (!fm.useContext || Object.keys(fm.useContext).length === 0) return questionnaire;

  const existing = questionnaire.useContext ?? [];
  const categoryCtx = existing.filter(
    (uc) => uc.code?.system === CONTEXT_SYSTEM && uc.code?.code === 'category',
  );

  const newContexts: UsageContext[] = Object.entries(fm.useContext).map(
    ([key, value]) => ({
      code: {
        system: CONTEXT_SYSTEM,
        code: key,
        display: key.charAt(0).toUpperCase() + key.slice(1),
      },
      valueCodeableConcept: {
        coding: [{ system: CONTEXT_SYSTEM, code: value, display: value }],
      },
    }),
  );

  return {
    ...questionnaire,
    useContext: [...categoryCtx, ...newContexts],
  };
}

/**
 * Check if a Questionnaire has the signature-required extension.
 */
export function hasSignatureRequired(questionnaire: Questionnaire): boolean {
  return (
    questionnaire.extension?.some((e) => e.url === SIGNATURE_REQUIRED_URL) ?? false
  );
}

/**
 * Read the document template category from a Questionnaire's meta.tag.
 */
export function getDocumentTemplateCategory(questionnaire: Questionnaire): string | undefined {
  return questionnaire.meta?.tag?.find(
    (t) => t.system === DOCUMENT_TEMPLATE_CATEGORY_SYSTEM,
  )?.code;
}
